import { CommonModule } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    HttpClientJsonpModule,
    HttpClientModule,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    APP_INITIALIZER,
    importProvidersFrom,
    ModuleWithProviders,
    NgModule,
    Optional,
    provideZoneChangeDetection,
    SkipSelf,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
    DEFAULT_IGNORE_FILESNAMES,
    DEFAULT_IGNORE_PATTERNS,
    ErrorHandlerInterceptor,
    ErrorHandlerService,
    KeycloakService,
    provideKeycloakAngular,
    provideLogger,
    SecureInterceptor,
    SPRINTPAY_SERVICES_TOKEN,
    SprintPayAPIsModule,
    TokenService,
} from '@cargos/sprintpay-services';
import { isDatadogEnabled } from '@cargos/sprintpay-tools';
import {
    BannerComponent,
    LIBRARY_MAT_FORM_FIELD_CONFIG,
    provideExternalLinksLaunch,
    TermsAndConditionsModalService,
} from '@cargos/sprintpay-ui';
import { RumEvent } from '@datadog/browser-rum';
import { browserTracingIntegration } from '@sentry/angular';
import { IMaskModule } from 'angular-imask';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HeaderComponent } from '../../components';
import { MaintenanceGateComponent } from '../../components/maintenance-gate/maintenance-gate.component';
import { FooterModule } from '../../components/footer/footer.module';
import { CatchEventsDirective } from '../../directives/catch-events.directive';
import { AuthGuard, CustomerTypeGuard } from '../../guards';
import { Config } from '../../models';
import { OrganizationsDefaultImplementationService } from '../../services/firebase/default-organizations-implementations.service';
import { OrganizationService } from '../../services/firebase/organizations.service';
import { ErrorCustomHandlerService } from '../../services/http-error-handler.service';
import { SessionWorkerService } from '../../services/session-worker';
import { AWBSubscriptionModule } from '../awb-subscription/awb-subscription.module';
import { FacilityInformationModule } from '../facility-information/facility-information.module';
import { InitialLoaderComponent } from '../initial-loader/initial-loader.component';
import { PaymentHistoryModule } from '../payment-history/payment-history.module';
import { TermsOfUseModule } from '../terms-of-use/terms-of-use.module';
import { CoreMaterialModule } from './core-material-modules.module';
import { appConfig } from './facility.config';
import { CoreConfig } from './models/core-types';
import { AppInitService } from './services/app-initializer.service';
import { scrubPII } from './utils/pii-scrubber';

const datadogEnabled = isDatadogEnabled(
    environment.datadog?.enabled ?? false,
    environment.datadog?.clientToken || '',
    environment.datadog?.applicationId || ''
);

const DATADOG_ENV_BY_ENVIRONMENT: Record<string, string> = {
    develop: 'qa',
    staging: 'uat',
    production: 'prod',
};

const environmentName = environment.environment ?? '';
const datadogEnv = DATADOG_ENV_BY_ENVIRONMENT[environmentName] ?? environmentName;

@NgModule({
    imports: [
        CatchEventsDirective,
        HeaderComponent,
        SprintPayAPIsModule,
        CoreMaterialModule,
        InitialLoaderComponent,
        BannerComponent,
        MaintenanceGateComponent,
    ],
    exports: [
        BrowserModule,
        BrowserAnimationsModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientJsonpModule,
        HttpClientModule,
        IMaskModule,
        FooterModule,
        FacilityInformationModule,
        CatchEventsDirective,
        HeaderComponent,
        AWBSubscriptionModule,
        PaymentHistoryModule,
        TermsOfUseModule,
        PaymentHistoryModule,
        InitialLoaderComponent,
        MaintenanceGateComponent,
    ],
})
export class CoreModule {
    constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
        if (parentModule) {
            throw new Error(
                `CoreModule is already loaded. It should only be imported in your application's main module.`
            );
        }
    }

    static forRoot(coreConfig?: CoreConfig): ModuleWithProviders<CoreModule> {
        let sprintPayProviders = SprintPayAPIsModule.forRoot({
            environment: environment.environment,
            tokenGetter: {
                tokenProvider: {
                    provide: SPRINTPAY_SERVICES_TOKEN,
                    useFactory: tokenFactory,
                    deps: [TokenService],
                },
            },
            config: {
                apisWithoutHeaders: coreConfig?.apisWithoutHeader || Config.apisWithoutHeader,
                apisWithoutContentType: coreConfig?.apisWithoutContentType || Config.apisWithoutContentType,
                apisWithoutAccessControl: coreConfig?.apisWithoutAccessControl || Config.apisWithoutAccessControl,
                allowedDomains: coreConfig?.allowedDomains || Config.allowedDomains,
                googleMapApiKey: environment.googleMapApiKey,
                cryptoKey: environment.cryptoKey,
                kioskFirebaseFunctions: environment.kioskFirebaseFunctions,
            },
        }).providers;

        sprintPayProviders = sprintPayProviders || [];

        return {
            ngModule: CoreModule,
            providers: [
                SessionWorkerService,
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: ErrorHandlerInterceptor,
                    multi: true,
                },
                ...sprintPayProviders,
                { provide: HTTP_INTERCEPTORS, useClass: SecureInterceptor, multi: true },
                { provide: ErrorHandlerService, useClass: ErrorCustomHandlerService },
                CustomerTypeGuard,
                AuthGuard,
                {
                    provide: OrganizationService,
                    useClass: OrganizationsDefaultImplementationService,
                },
                ...appConfig.providers,
                TermsAndConditionsModalService,
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeAppFactory,
                    deps: [AppInitService],
                    multi: true,
                },
                {
                    provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
                    useValue: coreConfig?.matFormFieldDefaultOptions || Config.matFormFieldDefaultOptions,
                },
                {
                    provide: LIBRARY_MAT_FORM_FIELD_CONFIG,
                    useExisting: MAT_FORM_FIELD_DEFAULT_OPTIONS,
                },
                provideKeycloakAngular(environment.keycloakConfig),
                KeycloakService,
                provideHttpClient(withInterceptorsFromDi()),
                provideZoneChangeDetection({ eventCoalescing: true }),
                importProvidersFrom(FormsModule, ReactiveFormsModule),
                ...provideExternalLinksLaunch(),
                provideLogger({
                    type: 'multi',
                    sentry: {
                        enabled: environment.enableSentry,
                        dsn: environment.appSentryDsn,
                        environment: environment.environment,
                        release: environment.appVersion,
                        ignorePatterns: DEFAULT_IGNORE_PATTERNS,
                        ignoreFilenames: DEFAULT_IGNORE_FILESNAMES.map((r) => r.source),
                        // Messages sent only to DataDog (not to Sentry)
                        ignoreErrors: [/^credit_card_verification_/],
                        integrations: [browserTracingIntegration()],
                        tracesSampleRate: 0.1,
                        tracePropagationTargets: ['https://sprintpay.cargosprint.com/'],
                    },
                    datadog: {
                        enabled: datadogEnabled, // Set to false to disable Datadog logging. Change to true to enable
                        site: environment.datadog?.site || '',
                        clientToken: environment.datadog?.clientToken || '',
                        service: environment.applicationName ?? '',
                        env: datadogEnv,
                        version: environment.appVersion,
                        forwardErrorsToLogs: environment.datadog?.logger?.forwardErrorsToLogs ?? true,
                        sessionSampleRate: environment.datadog?.logger?.sessionSampleRate ?? 100,
                        rum: {
                            applicationId: environment.datadog?.applicationId || '',
                            clientToken: environment.datadog?.clientToken || '',
                            service: environment.applicationName || '',
                            site: environment.datadog?.site || '',
                            env: datadogEnv,
                            version: environment.appVersion,
                            sessionSampleRate: environment.datadog?.rum?.sessionSampleRate ?? 100,
                            sessionReplaySampleRate: environment.datadog?.rum?.sessionReplaySampleRate ?? 20,
                            trackResources: environment.datadog?.rum?.trackResources || true,
                            trackUserInteractions: environment.datadog?.rum?.trackUserInteractions || true,
                            trackLongTasks: environment.datadog?.rum?.trackLongTasks || true,
                            defaultPrivacyLevel: 'mask-user-input',
                            beforeSend: (event: RumEvent) => {
                                if (event.type === 'error' && event.error.message) {
                                    event.error.message = scrubPII(event.error.message);
                                }
                                if (event.type === 'view' && event.view.url) {
                                    (event.view as { url: string }).url = scrubPII(event.view.url);
                                }
                                if (event.type === 'resource' && event.resource.url) {
                                    (event.resource as { url: string }).url = scrubPII(event.resource.url);
                                }
                                return true;
                            },
                        },
                    },
                    enableErrorHandler: true,
                    enableSentryTracing: true,
                }),
            ],
        };
    }
}

export function tokenFactory(tokenService: TokenService): () => string {
    return () => tokenService.instant_token;
}

export function initializeAppFactory(appInitService: AppInitService): () => Promise<void> {
    return () => firstValueFrom(appInitService.initializeApp());
}
